import { flash } from "@xola/ui-kit";
import { FormField, SelectField, SwitchField } from "common";
import { FormikProps } from "formik";
import { get } from "lodash";
import React, { forwardRef, Ref } from "react";
import { useMutation } from "react-query";
import { BookingPreference, Permission, SelectedExperiences, Seller, SellerMeta } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { IsGranted } from "../../Common/IsGranted";
import { TimePickerPopover } from "../../Common/TimePickerPopover";
import { XolaFormik } from "../../Common/XolaFormik";
import { AutoAcceptPreferences } from "./AutoAcceptPreferences";
import { PriceStrategy } from "@/components/PurchaseModification/PaymentBreakdown";

const postBookingActions = [
    { label: "Manage booking", value: "manageBooking" },
    { label: "Go To Dashboard", value: "goToDashboard" },
    { label: "Make another reservation", value: "createOrder" },
];

const emailRequiredPath = "meta.xola/seller/make_order/email_required";

const usePurchases = () => {
    const { isGranted } = useAuthorization();
    const isSellerUser = isGranted(Permission.PREFERENCES_SELLER_VIEW);
    const isAdminUser = isGranted(Permission.PREFERENCES_ADMIN_VIEW);

    return [
        {
            label: "Modify OTA bookings",
            name: "preferences.booking.allowBookingEngineOrderModification",
            children: "Allow reservationists to modify purchases that originated from online distribution channels",
            visible: isAdminUser,
        },
        {
            label: "Allow Past Dates",
            name: "preferences.booking.allowPastDates",
            children: "Allow reservationists to create new purchases for past dates",
            visible: isAdminUser,
        },
        {
            label: "Email Required",
            name: emailRequiredPath,
            children:
                'Require reservationists to either enter an email address or select "Email not provided" while creating purchases',
            visible: isSellerUser,
        },
        {
            label: "Phone Number Required",
            name: "meta.xola/seller/make_order/phone_required",
            children:
                'Require reservationists to either enter a phone number or select "Phone number not provided" while creating purchases',
            visible: isSellerUser,
        },
    ];
};

export const PurchasesPreferences = forwardRef((_props, ref: Ref<FormikProps<Seller>>) => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const purchases = usePurchases();

    const { isGranted } = useAuthorization();

    const isNoteAutoExpandGranted = isGranted(Permission.PREFERENCES_ROSTER_AUTO_EXPAND_NOTES);

    const { mutateAsync: updateMeta } = useMutation(async (meta: SellerMeta) => {
        if (seller.meta !== meta) {
            await api.post(`/sellers/${seller.id}/meta`, meta);
        }
    });

    const { mutateAsync: updateBookingPreference } = useMutation(async (preference: BookingPreference) => {
        if (seller.preferences.booking !== preference) {
            return api.post(`/users/${seller.id}/preferences/booking`, preference);
        }
    });

    const { mutateAsync: updateAutoAccept } = useMutation(async (autoAccept: SelectedExperiences) => {
        if (seller.autoAccept !== autoAccept) {
            return api.patch(`/sellers/${seller.id}`, { autoAccept });
        }
    });

    const getInitialValues = () => {
        const initialValues = { ...seller };

        // By default xola/seller/roster/autoExpandNotes should be true
        if ("xola/seller/roster/autoExpandNotes" in seller.meta) {
            return initialValues;
        }

        return { ...initialValues, meta: { ...initialValues.meta, "xola/seller/roster/autoExpandNotes": true } };
    };

    const handleSubmit = async (values: Seller) => {
        await Promise.all([
            updateMeta(values.meta),
            updateBookingPreference(values.preferences.booking),
            updateAutoAccept(values.autoAccept),
        ]);

        invalidate("sellers", seller.id);
        flash.success("Successfully saved Purchase workflow settings");
    };

    return (
        <XolaFormik enableReinitialize innerRef={ref} initialValues={getInitialValues()} onSubmit={handleSubmit}>
            {({ values, isSubmitting, setFieldValue }) => {
                const autoCheckIn = values.meta["xola/seller/quickCheckIn/autoCheckInOffset"] ?? null;
                const autoCheckInEnabled = autoCheckIn !== null;
                const defaultPriceStrategyForPurchaseEdit =
                    get(values, "preferences.booking.defaultPriceStrategyForPurchaseEdit") ?? PriceStrategy.RETAIN;

                return (
                    <fieldset disabled={isSubmitting}>
                        <AutoAcceptPreferences />

                        <IsGranted permissions={Permission.PREFERENCES_SELLER_VIEW}>
                            <div className="flex items-center" data-testid="purchases-tab-auto-check-in-section">
                                <FormField
                                    name="meta.xola/seller/quickCheckIn/autoCheckInOffset"
                                    label="Auto Check-in"
                                    tooltip="Automatically check-in the customer of a back office booking if it is within the specified interval of the arrival time"
                                >
                                    <SwitchField
                                        isChecked={autoCheckInEnabled}
                                        onChange={(isChecked) => {
                                            setFieldValue(
                                                "meta.xola/seller/quickCheckIn/autoCheckInOffset",
                                                isChecked ? 0 : null,
                                            );
                                        }}
                                    >
                                        Auto check-in customers {autoCheckInEnabled ? "if they book within " : null}
                                    </SwitchField>
                                </FormField>

                                {autoCheckInEnabled ? (
                                    <p className="font-normal text-gray-darker">
                                        <TimePickerPopover
                                            value={autoCheckIn}
                                            onApply={(value) => {
                                                setFieldValue("meta.xola/seller/quickCheckIn/autoCheckInOffset", value);
                                            }}
                                        />
                                        of the arrival time
                                    </p>
                                ) : null}
                            </div>
                        </IsGranted>

                        <FormField
                            name="preferences.booking.postBookingAction"
                            label="Default Post Purchase Action"
                            tooltip="Chose the next course of action after payment is successfully completed for a trip. You can now navigate to dashboard, manage the booking or make another reservation."
                        >
                            <SelectField>
                                {postBookingActions.map((action) => (
                                    <option key={action.value} value={action.value}>
                                        {action.label}
                                    </option>
                                ))}
                            </SelectField>
                        </FormField>

                        {purchases.map(({ name, label, children, visible }) =>
                            visible ? (
                                <div key={name} data-testid={`purchases-tab-${label}-section`}>
                                    <FormField label={label} name={name}>
                                        <SwitchField
                                            // By default we want to keep email required as true, same behaviour as v1
                                            isChecked={
                                                name === emailRequiredPath && get(values, name) === undefined
                                                    ? true
                                                    : get(values, name)
                                            }
                                            onChange={(value) => setFieldValue(name, value)}
                                        >
                                            {children}
                                        </SwitchField>
                                    </FormField>
                                </div>
                            ) : null,
                        )}

                        <IsGranted permissions={Permission.PREFERENCES_ADMIN_VIEW}>
                            <div key="Retain Current Price Configuration">
                                <FormField
                                    label="Retain Current Price Configuration"
                                    name="preferences.booking.defaultPriceStrategyForPurchaseEdit"
                                >
                                    <SwitchField
                                        isChecked={defaultPriceStrategyForPurchaseEdit === PriceStrategy.RETAIN}
                                        onChange={(value) => {
                                            const priceStrategy = value ? PriceStrategy.RETAIN : PriceStrategy.LATEST;
                                            setFieldValue(
                                                "preferences.booking.defaultPriceStrategyForPurchaseEdit",
                                                priceStrategy,
                                            );
                                        }}
                                    >
                                        <span>
                                            Turn on <b>Retain current price</b> when making purchase modifications
                                        </span>
                                    </SwitchField>
                                </FormField>
                            </div>
                        </IsGranted>

                        <FormField name="meta.xola/seller/roster/autoExpandNotes" label="Expand Notes Section">
                            <SwitchField
                                isDisabled={!isNoteAutoExpandGranted}
                                isChecked={get(values, "meta.xola/seller/roster/autoExpandNotes")}
                                onChange={(isChecked) =>
                                    setFieldValue("meta.xola/seller/roster/autoExpandNotes", isChecked)
                                }
                            >
                                Expand the notes section in the roster and purchase details when notes are present
                            </SwitchField>
                        </FormField>
                    </fieldset>
                );
            }}
        </XolaFormik>
    );
});
